import { useEffect } from "react";
import { SITE_URL, BUSINESS } from "@/data/site";

/**
 * useSEO — dynamic per-page SEO helper for a SPA.
 *
 * Sets:
 *  - <title>
 *  - meta description
 *  - canonical link (defaults to current window.location.href stripped of query/hash)
 *  - Open Graph (og:title, og:description, og:image, og:url, og:type, og:site_name)
 *  - Twitter Card (summary_large_image)
 *  - meta robots (optional)
 *  - one or more JSON-LD scripts injected under a stable id and cleaned up on unmount
 *
 * All tags are updated in-place rather than duplicated on route change.
 */
export default function useSEO({
  title,
  description,
  canonical,
  image,
  type = "website",
  robots,
  jsonld,
} = {}) {
  useEffect(() => {
    if (title) document.title = title;

    setMeta("description", description);

    // Canonical
    const canonicalHref = canonical || defaultCanonical();
    setLink("canonical", canonicalHref);

    // Open Graph
    setMetaProp("og:site_name", BUSINESS.name);
    setMetaProp("og:title", title || document.title);
    setMetaProp("og:description", description);
    setMetaProp("og:url", canonicalHref);
    setMetaProp("og:type", type);
    setMetaProp("og:image", image || `${SITE_URL}/electromax-logo-dark.png`);
    setMetaProp("og:locale", "en_CA");

    // Twitter Card
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title || document.title);
    setMeta("twitter:description", description);
    setMeta("twitter:image", image || `${SITE_URL}/electromax-logo-dark.png`);

    // Robots — respect explicit page-level override, otherwise fall back to
    // the site-wide indexing flag. When REACT_APP_ALLOW_INDEXING !== "true"
    // (the default, ie. preview / client-review environments), every route
    // emits noindex,nofollow so nothing gets picked up by search engines.
    const allowIndexing = process.env.REACT_APP_ALLOW_INDEXING === "true";
    const effectiveRobots =
      robots || (allowIndexing ? "index,follow,max-image-preview:large,max-snippet:-1" : "noindex,nofollow,noarchive");
    setMeta("robots", effectiveRobots);
    setMeta("googlebot", effectiveRobots);

    // JSON-LD — accept a single object or an array of objects
    const items = Array.isArray(jsonld) ? jsonld : jsonld ? [jsonld] : [];
    items.forEach((obj, i) => setJsonLd(`page-jsonld-${i}`, obj));

    // Cleanup extra jsonld tags if fewer this render than last
    for (let i = items.length; i < 8; i += 1) {
      const stale = document.getElementById(`page-jsonld-${i}`);
      if (stale) stale.remove();
    }
  }, [title, description, canonical, image, type, robots, JSON.stringify(jsonld)]);
}

function setMeta(name, content) {
  if (content === undefined || content === null) return;
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", String(content));
}

function setMetaProp(property, content) {
  if (content === undefined || content === null) return;
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", String(content));
}

function setLink(rel, href) {
  if (!href) return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function defaultCanonical() {
  if (typeof window === "undefined") return SITE_URL;
  const { origin, pathname } = window.location;
  // Prefer the canonical origin (electromax.ca) over the preview host so
  // Googlebot doesn't index the preview URL.
  const base = SITE_URL || origin;
  return `${base}${pathname.replace(/\/$/, "") || "/"}`;
}
